// Pulls a safe, always-an-object error payload out of a failed axios
// request. Plain `err.response.data` throws when the request never got a
// response at all (server down, timeout, CORS block, network drop) —
// err.response is undefined in that case, so accessing .data blows up
// inside the very catch block that's supposed to handle the error. That
// thrown error then gets swallowed by whatever try/catch is further up the
// call stack, which means the intended dispatch(setError(...)) never runs
// — leaving Redux's error state stuck at whatever it was (or worse, since
// some call sites do `err.response.data || fallback`, which still throws
// before the `||` ever gets a chance to apply the fallback).
//
// Every reducer that talks to the API should route through this instead of
// touching err.response.data directly.
export function getErrorData(err, fallbackMessage = 'Something went wrong. Please try again.') {
  if (err && err.response && err.response.data && typeof err.response.data === 'object') {
    return err.response.data;
  }
  return { error: fallbackMessage };
}

// Turns an error payload (the object shape returned by getErrorData / the
// backend, e.g. { email: 'Invalid email or password' } or
// { error: 'Server error during login' }) into a single human-readable
// string, so toasts can show the ACTUAL reason a request failed instead of
// a generic "check all fields" message.
//
// Field-keyed errors (email, password, mobile, ...) are joined together so
// the toast reads like "Invalid email or password" rather than just
// echoing the field name. A bare { error: '...' } / { message: '...' }
// payload is returned as-is. Falls back to fallbackMessage only when the
// payload has nothing usable in it (e.g. {} or a non-object).
export function getErrorMessage(errorObj, fallbackMessage = 'Something went wrong. Please try again.') {
  if (!errorObj || typeof errorObj !== 'object') return fallbackMessage;

  if (typeof errorObj.error === 'string' && errorObj.error.trim()) return errorObj.error;
  if (typeof errorObj.message === 'string' && errorObj.message.trim()) return errorObj.message;

  const fieldMessages = Object.keys(errorObj)
    .filter((key) => key !== 'error' && key !== 'message')
    .map((key) => errorObj[key])
    .filter((val) => typeof val === 'string' && val.trim());

  if (fieldMessages.length) {
    // De-dupe identical messages (e.g. backend sending the same
    // "Invalid email or password" under both email and password)
    return Array.from(new Set(fieldMessages)).join(' ');
  }

  return fallbackMessage;
}